// @flow
import React, { Fragment, PureComponent } from 'react';
import { Image, ScrollView, StyleSheet, View } from 'react-native';
import { ActivityIndicator, EmphasizedSubText, Text } from 'trpc-react-native-ui';
import type { NavigationRoute, NavigationScreenProp } from '@react-navigation/compat';
import type { ReferralBonuses } from 'digicel-kaizen-library';

import { getActivationInitialRouteName } from '../../../lib/navigation';
import theme from '../../../theme-config';
import { FullButton, GradientPage, SecondaryFullButton } from '../../../components/common';
import I18n from '../../../lib/i18n';
import config from '../../../config';
import { formatGodchildBonuses } from '../../../lib/bonuses';

export default class FriendInvitation extends PureComponent<Props> {
  styles = getStyles();

  goToOrderSim = () => {
    if (this.props.route.params?.code && this.props.referralBonuses) {
      this.props.setGodfatherExternalId(this.props.route.params?.code);
    }
    this.props.navigation.push('requiredToSignUp');
  };
  goToActivateSim = () => this.props.navigation.replace(getActivationInitialRouteName());

  componentDidMount() {
    this.props.fetchReferralBonuses();
    if (!this.props.route.params?.code || !this.props.route.params?.name) {
      this.goToOrderSim();
      return;
    }

    if (!!this.props.referralBonuses) {
      this.props.verifyGodfather(this.props.route.params?.code);
    }
  }

  renderText = () => {
    const styles = this.styles;

    if (!this.props.referralBonuses) {
      return (
        <View>
          <Text style={[styles.text, styles.invalidText]}>{I18n.t('SignUp.FriendInvitation.referralExpired')}</Text>
        </View>
      );
    }

    if (this.props.isGodfatherInvalid) {
      return (
        <View>
          <Text style={[styles.text, styles.invalidText]} testID={'friendInvitationInvalidText'}>
            {this.props.godfatherValidityError}
          </Text>
        </View>
      );
    }

    return (
      <View>
        <Text style={[styles.text, styles.friendInvitationText]} testID={'friendInvitationValidText'}>
          {I18n.t('SignUp.FriendInvitation.text', { name: this.props.route.params?.name })}
        </Text>
        {!!this.props.referralBonuses && !this.props.referralBonuses.isDiscount && this.props.referralBonuses.godchild && (
          <EmphasizedSubText emphStyle={styles.bonus} style={styles.secondText}>
            {I18n.t('SignUp.FriendInvitation.secondText', {
              bonus: formatGodchildBonuses(this.props.referralBonuses.godchild),
            })}
          </EmphasizedSubText>
        )}
      </View>
    );
  };

  render() {
    const styles = this.styles;
    return (
      <GradientPage backgroundImage={theme.upDownStrokesBackgroundImage}>
        <View style={styles.pageContainer}>
          <ScrollView contentContainerStyle={styles.contentContainer}>
            <Image
              source={!this.props.isGodfatherInvalid ? theme.inviteAFriend : theme.invalidInviteAFriend}
              style={styles.image}
              resizeMode="contain"
            />
            {this.renderText()}
          </ScrollView>
          <View style={styles.footer}>
            {!this.props.isInitializing ? (
              <Fragment>
                <FullButton
                  testID={'friendInvitationContinueButton'}
                  onPress={this.goToOrderSim}
                  style={styles.button}
                  text={I18n.t('SignUp.FriendInvitation.continue')}
                />
                {config.displayActivateSimButtonOnInvitation && (
                  <SecondaryFullButton
                    onPress={this.goToActivateSim}
                    style={styles.button}
                    text={I18n.t('SignUp.FriendInvitation.activate')}
                  />
                )}
              </Fragment>
            ) : (
              <ActivityIndicator />
            )}
          </View>
        </View>
      </GradientPage>
    );
  }
}

const getStyles = () =>
  StyleSheet.create({
    bonus: {
      fontWeight: '600',
    },
    footer: {
      marginBottom: 16,
    },
    friendInvitationText: {
      fontWeight: 'bold',
      marginVertical: 14,
    },
    button: {
      marginTop: 16,
    },
    contentContainer: {
      alignItems: 'center',
      margin: 5,
      paddingBottom: 10,
    },
    image: {
      height: 150,
      width: 200,
      marginBottom: 16,
    },
    pageContainer: {
      flex: 1,
      justifyContent: 'space-between',
      marginHorizontal: 22,
    },
    secondText: {
      fontSize: 20,
      marginHorizontal: 20,
      marginTop: 10,
      textAlign: 'center',
    },
    sender: {
      color: theme.gray.normal,
      fontSize: 18,
      marginBottom: 14,
      marginHorizontal: 45,
      marginTop: 14,
      textAlign: 'center',
    },
    text: {
      fontSize: 22,
      marginHorizontal: 20,
      textAlign: 'center',
    },
    invalidText: {
      color: theme.gray.normalLight,
    },
  });

export type StateProps = {
  godfatherValidityError: ?string,
  isGodfatherInvalid: boolean,
  isInitializing: boolean,
  referralBonuses: ?ReferralBonuses,
};

type Props = {|
  fetchReferralBonuses: Function,
  navigation: NavigationScreenProp<NavigationRoute>,
  route: NavigationScreenProp<NavigationRoute>,
  setGodfatherExternalId: Function,
  verifyGodfather: Function,
|} & StateProps;
